import { Link } from "react-router-dom";

export default function HeroSection() {
  return (
    <section className="relative w-full overflow-hidden" style={{ height: "100vh", minHeight: "820px" }}>

      {/* ── Background Image ─────────────────────────────────── */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/f556b3fd1694b41e0e87de4ff48abcdf.jpg"
          alt="Mother cradling newborn — Precious Keepsakes Photography San Antonio"
          className="w-full h-full object-cover object-center"
        />
        {/* Deep dramatic gradient layers */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/35 to-black/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-black/30" />
        {/* Warm amber tint overlay — ties to brand gold */}
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 60% 40%, rgba(180,130,60,0.08) 0%, transparent 65%)" }} />
      </div>

      {/* ── Top eyebrow location strip ───────────────────────── */}
      <div className="absolute top-28 left-0 right-0 flex justify-center z-10">
        <div className="animate-fade-in delay-100 flex items-center gap-5">
          <div className="w-14 h-px bg-pk-gold/50 animate-line-grow" />
          <span className="font-sans text-[10px] tracking-widest3 uppercase text-pk-gold/80 letter-spacing-wider">
            Fine Art Keepsake Photography · San Antonio, Texas
          </span>
          <div className="w-14 h-px bg-pk-gold/50 animate-line-grow delay-200" />
        </div>
      </div>

      {/* ── Center Content ───────────────────────────────────── */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-8">

        {/* Eyebrow tag */}
        <div className="animate-fade-up mb-6">
          <span className="inline-block font-sans text-[10px] tracking-widest3 uppercase text-pk-gold/90 border border-pk-gold/30 px-5 py-2 rounded-full backdrop-blur-sm bg-white/5">
            Precious Keepsakes Photography
          </span>
        </div>

        {/* Main Headline — legacy/memory focused */}
        <h1 className="animate-fade-up delay-100 font-display font-light text-white leading-none max-w-5xl text-balance"
          style={{ fontSize: "clamp(3.2rem, 8vw, 7.5rem)" }}>
          Preserve the Moments<br />
          <em className="font-medium italic text-pk-gold">That Define Your Legacy</em>
        </h1>

        {/* Divider ornament */}
        <div className="animate-fade-up delay-200 flex items-center gap-5 mt-9">
          <div className="w-16 h-px bg-white/20" />
          <div className="flex gap-1.5">
            <div className="w-1 h-1 rounded-full bg-pk-gold/70" />
            <div className="w-1.5 h-1.5 rounded-full bg-pk-gold" />
            <div className="w-1 h-1 rounded-full bg-pk-gold/70" />
          </div>
          <div className="w-16 h-px bg-white/20" />
        </div>

        {/* Subheadline */}
        <p className="animate-fade-up delay-300 font-sans text-base md:text-lg text-white/70 mt-7 max-w-[520px] leading-8 font-light">
          Heirloom-quality portraits of newborns, weddings, and life&rsquo;s most sacred milestones &mdash;
          crafted to be passed down through generations.
        </p>

        {/* CTA Group */}
        <div className="animate-fade-up delay-500 mt-12 flex flex-col sm:flex-row items-center gap-5">
          <Link
            to="/services"
            className="group relative overflow-hidden bg-pk-gold text-white font-sans text-xs tracking-widest3 uppercase px-14 py-5 rounded-sm whitespace-nowrap cursor-pointer"
            style={{ transition: "background 0.4s, color 0.4s" }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "#fff"; (e.currentTarget as HTMLElement).style.color = "#1a1612"; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ""; (e.currentTarget as HTMLElement).style.color = ""; }}
          >
            Create Your Custom Keepsake
          </Link>
          <Link
            to="/about"
            className="flex items-center gap-3 font-sans text-sm text-white/60 hover:text-white transition-colors duration-300 whitespace-nowrap cursor-pointer group"
          >
            <span className="w-8 h-8 flex items-center justify-center border border-white/20 rounded-full group-hover:border-pk-gold group-hover:text-pk-gold transition-colors duration-300">
              <i className="ri-play-mini-fill text-xs" />
            </span>
            Meet Stephanie
          </Link>
        </div>

        {/* Trust pills */}
        <div className="animate-fade-up delay-700 mt-11 flex flex-wrap justify-center gap-3">
          {[
            { icon: "ri-award-line", label: "15+ Years Mastery" },
            { icon: "ri-star-fill", label: "5-Star Rated Studio" },
            { icon: "ri-heart-line", label: "500+ Families" },
            { icon: "ri-shield-check-line", label: "Heirloom Guarantee" },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2 bg-white/8 backdrop-blur-sm border border-white/10 rounded-full px-4 py-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${item.icon} text-pk-gold`} style={{ fontSize: "11px" }} />
              </div>
              <span className="font-sans text-[11px] text-white/75 tracking-wide">{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Scroll indicator ─────────────────────────────────── */}
      <div className="animate-fade-in delay-800 absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2.5">
        <span className="font-sans text-[10px] tracking-widest3 uppercase text-white/25">Scroll</span>
        <div className="relative w-px h-12 bg-white/10 overflow-hidden">
          <div className="absolute top-0 w-full bg-pk-gold/70 animate-scroll-line" style={{
            height: "40%",
            animation: "scrollDrop 2s ease-in-out infinite"
          }} />
        </div>
      </div>

      {/* ── Bottom-left logo stamp ────────────────────────────── */}
      <div className="animate-fade-in delay-600 absolute bottom-8 left-8 hidden md:flex items-center gap-3">
        <img
          src="https://images-pw.pixieset.com/profile/632184/8e2e397ed4499187d962a6bd1d1c7a5bdb31a2144ecd3aa53b6892fc200e0365.png"
          alt="Precious Keepsakes Photography"
          className="h-7 w-auto opacity-50 object-contain filter brightness-0 invert"
        />
      </div>

      {/* ── Phone — bottom right ─────────────────────────────── */}
      <div className="animate-fade-in delay-700 absolute bottom-8 right-8 hidden md:flex items-center gap-2">
        <a href="tel:2108154698" className="flex items-center gap-2 font-sans text-xs text-white/40 hover:text-pk-gold transition-colors duration-300 cursor-pointer">
          <i className="ri-phone-line text-pk-gold text-sm" />
          210-815-4698
        </a>
      </div>

      <style>{`
        @keyframes scrollDrop {
          0%   { transform: translateY(-100%); opacity: 1; }
          80%  { transform: translateY(300%); opacity: 0.5; }
          100% { transform: translateY(300%); opacity: 0; }
        }
      `}</style>
    </section>
  );
}
